  class CartToast extends HTMLElement {
    constructor() {
      super();
      this.headingEl = this.querySelector('.cart-toast__heading');
      this.closeBtn = this.querySelector('.cart-toast__close');
      this.itemsContainer = this.querySelector('.cart-toast__items');
      this.footerEl = this.querySelector('.cart-toast__footer');
      this.timeout = null;

      // Snapshot the default heading + cart footer so non-cart modes
      // (redemption flow) can restore them when the next cart-add toast fires.
      this.defaultHeading = this.headingEl ? this.headingEl.textContent : '';
      this.defaultFooterHTML = this.footerEl ? this.footerEl.innerHTML : '';

      this.closeBtn.addEventListener('click', () => this.hide());
    }

    // Custom message mode — used by the rewards redemption flow so the
    // success/in-flight state appears in the same top-right popup as cart
    // adds, instead of the page-bottom kk-toast banner. Keep all values
    // we splice into the DOM as text (or DOM-built nodes) — never trust
    // server-controlled strings in innerHTML.
    renderMessage(opts) {
      opts = opts || {};
      if (this.headingEl) this.headingEl.textContent = opts.heading || this.defaultHeading;
      // Message mode takes the items list out of its scroll container — see the
      // `.cart-toast--message` note in sections/cart-toast.liquid (the busy
      // spinner's rotation is scrollable overflow, which made the scrollbar,
      // and its stepper arrows, flicker several times a second).
      this.classList.add('cart-toast--message');
      this.itemsContainer.innerHTML = '';

      const row = document.createElement('div');
      row.className = 'cart-toast__message';
      if (opts.busy) {
        row.setAttribute('aria-busy', 'true');
        // Real spinner element (same .kk-spinner as the buttons) so it lays
        // out inline with the body text, not stranded on its own line.
        const sp = (window.KKBtn && window.KKBtn.spinnerEl) ? window.KKBtn.spinnerEl() : document.createElement('span');
        sp.className = 'kk-spinner';
        sp.setAttribute('aria-hidden', 'true');
        row.appendChild(sp);
      }

      if (opts.body) {
        const body = document.createElement('div');
        body.className = 'cart-toast__message-body';
        body.textContent = opts.body;
        row.appendChild(body);
      }
      if (opts.code) {
        const code = document.createElement('div');
        code.className = 'cart-toast__message-code';
        code.textContent = opts.code;
        row.appendChild(code);
      }
      this.itemsContainer.appendChild(row);

      if (this.footerEl) {
        // Only show the footer (e.g. "Use it now") once there's a completed
        // action to offer. During the busy state there is none — clear AND
        // hide it so a stale action button from a prior render can't linger.
        if (opts.action && opts.action.copy && opts.action.label) {
          // A code to copy rather than a link to follow (a free-shipping code is entered at checkout,
          // never applied to the cart). The global KKCopy button (assets/kkumi-copy.js) does the
          // copying and the "Copied" label; both words come from the caller's merchant copy.
          this.footerEl.innerHTML = '';
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'button button--primary';
          b.setAttribute('data-kk-copy', opts.action.copy);
          if (opts.action.copied) b.setAttribute('data-kk-copied', opts.action.copied);
          const label = document.createElement('span');
          label.setAttribute('data-kk-copy-label', '');
          label.textContent = opts.action.label;
          b.appendChild(label);
          this.footerEl.appendChild(b);
          this.footerEl.removeAttribute('hidden');
        } else if (opts.action && opts.action.href && opts.action.label) {
          this.footerEl.innerHTML = '';
          const a = document.createElement('a');
          a.href = opts.action.href;
          a.className = 'button button--primary';
          // Label stays a text node — KKIcons.labelInto() sets it with
          // textContent and appends the trailing arrow as an element, so the
          // action reads as a real vector arrow without any runtime string
          // reaching innerHTML (twin of snippets/kkumi-label-arrow.liquid).
          if (window.KKIcons && window.KKIcons.labelInto) window.KKIcons.labelInto(a, opts.action.label);
          else a.textContent = opts.action.label;
          this.footerEl.appendChild(a);
          this.footerEl.removeAttribute('hidden');
        } else {
          this.footerEl.innerHTML = '';
          this.footerEl.setAttribute('hidden', '');
        }
      }

      // opts.duration (ms) keeps a popup the member has to act on (copy a code) up longer than the default 5 s.
      this.show({ autohide: opts.busy ? false : true, duration: opts.duration });
    }

    // Restore cart-add layout (heading + Cart/Checkout footer) before
    // rendering the next /cart/add result. Called from renderItems().
    restoreCartLayout() {
      this.classList.remove('cart-toast--message');
      if (this.headingEl) this.headingEl.textContent = this.defaultHeading;
      if (this.footerEl) {
        this.footerEl.innerHTML = this.defaultFooterHTML;
        this.footerEl.removeAttribute('hidden');
      }
    }

    renderItems(addedItems) {
      this.restoreCartLayout();
      this.itemsContainer.innerHTML = '';
      const signatureVariantId = Number(this.dataset.excludeVariant) || 0;

      const filteredItems = addedItems.filter(item => item.id !== signatureVariantId && item.variant_id !== signatureVariantId);

      if (filteredItems.length === 0) {
        this.hide();
        return;
      }

      filteredItems.forEach(item => {
        const quantity = item.added_quantity || 1;
        const priceFormatted = window.KKMoney.fromCents(item.price);
        const totalFormatted = window.KKMoney.fromCents(item.price * quantity);
        
        let priceDisplay = '';
        if (quantity === 1) {
          priceDisplay = priceFormatted;
        } else {
          priceDisplay = `${quantity}x ${priceFormatted} (${totalFormatted})`;
        }
        
        // Built with DOM APIs (not HTML strings) so product titles/images can't inject markup
        const itemEl = document.createElement('div');
        itemEl.className = 'cart-toast__item';

        const img = document.createElement('img');
        img.src = item.image || '';
        img.alt = item.title || '';
        img.className = 'cart-toast__item-image';
        itemEl.appendChild(img);

        const details = document.createElement('div');
        details.className = 'cart-toast__item-details';

        const title = document.createElement('span');
        title.className = 'cart-toast__item-title';
        title.textContent = item.product_title || item.title || '';
        details.appendChild(title);

        const price = document.createElement('span');
        price.className = 'cart-toast__item-price';
        price.textContent = priceDisplay;
        details.appendChild(price);

        itemEl.appendChild(details);
        this.itemsContainer.appendChild(itemEl);
      });

      this.show();
    }

    show(opts) {
      opts = opts || {};
      this.classList.add('is-active');
      this.setAttribute('aria-hidden', 'false');

      if (this.timeout) clearTimeout(this.timeout);
      if (opts.autohide !== false) {
        this.timeout = setTimeout(() => this.hide(), opts.duration || 5000);
      }
    }

    hide() {
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
    }
  }

  if (!customElements.get('cart-toast')) {
    customElements.define('cart-toast', CartToast);
  }

  document.addEventListener('cart:added', (event) => {
    const toastElement = document.querySelector('cart-toast');
    if (toastElement && event.detail.items && event.detail.items.length > 0) {
      toastElement.renderItems(event.detail.items);
    }
  });

  if (!window.kkumiFetchPatched && !window.location.href.includes('/kkumi-club')) {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const arg0 = args[0];
      const url = arg0 instanceof Request ? arg0.url
                : arg0 instanceof URL ? arg0.href
                : (typeof arg0 === 'string' ? arg0 : '');
      const response = await originalFetch(...args);

      // An in-place edit (the cart line variant editor) re-adds a line it is
      // about to replace; it tags the request so no "Added to cart" popup fires.
      let silent = false;
      try {
        const reqInit = arg0 instanceof Request ? arg0 : args[1];
        silent = !!(reqInit && reqInit.headers && new Headers(reqInit.headers).get('X-Kkumi-Silent'));
      } catch (e) {}

      if (url && url.includes('/cart/add') && !silent) {
        let addedDict = {};
        try {
          const opts = args[0] instanceof Request ? args[0] : args[1];
          const body = opts ? opts.body : null;
          
          if (body) {
            if (body instanceof FormData) {
              const id = body.get('id');
              const qty = body.get('quantity') || 1;
              if (id) addedDict[id.toString()] = parseInt(qty, 10);
            } else if (typeof body === 'string') {
              try {
                const parsed = JSON.parse(body);
                if (parsed.items) {
                  parsed.items.forEach(i => {
                    addedDict[i.id.toString()] = parseInt(i.quantity || 1, 10);
                  });
                } else if (parsed.id) {
                  addedDict[parsed.id.toString()] = parseInt(parsed.quantity || 1, 10);
                }
              } catch(e) {
                const params = new URLSearchParams(body);
                const id = params.get('id');
                const qty = params.get('quantity') || 1;
                if (id) addedDict[id.toString()] = parseInt(qty, 10);
              }
            }
          }
        } catch(e) {}

        const clonedResponse = response.clone();
        clonedResponse.json().then(data => {
          const items = data.items ? data.items : [data];
          
          const enrichedItems = items.map(item => {
            const variantIdStr = item.variant_id.toString();
            const addedQty = addedDict[variantIdStr] || addedDict[item.id.toString()] || 1;
            return {
              ...item,
              added_quantity: addedQty
            };
          });

          document.dispatchEvent(new CustomEvent('cart:added', { 
            detail: { items: enrichedItems } 
          }));
        }).catch(e => {});
      }
      return response;
    };
    window.kkumiFetchPatched = true;
  }
